import { assign } from "lodash";
import { ChangeDetectionStrategy, Component, ElementRef, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from "@medsurf/flat-facades";
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-question',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './question.component.html',
    styleUrls: ['./question.component.scss'],
    standalone: false
})
export class QuestionComponent {
  @ViewChild('edit_overlay_template_scroll_panel', {static: false}) editPanelScrollPanel: ElementRef<HTMLDivElement>;
  
  #isSubmitted = signal(false);
  question: FlatModels.QuestionEntityModels.QuestionEntityType | null;
  questionForm = new FormGroup(
    {
      type: new FormControl<FlatModels.QuestionEntityModels.QuestionType | null>(null),
      customEnumeration: new FormControl(''),
      required: new FormControl(false),
      numberingType: new FormControl(''),
      randomised: new FormControl(false),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.QuestionEntityModels.QuestionSchema.pick({
          type: true,
          customEnumeration: true,
          required: true,
          numberingType: true,
          randomised: true
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade) {
    // Update Layer
    this.slideAuthorFacade.currentSelectedSlideQuestion$.pipe(takeUntilDestroyed()).subscribe((question) => {
      if (this.question?.id !== question?.id && this.editPanelScrollPanel) {
        this.editPanelScrollPanel.nativeElement.scrollTop = 0;
      }
      this.question = structuredClone(question);
      if (this.question) {
        const { type, customEnumeration, required, randomised, numberingType } = this.question;
        this.questionForm.reset({ type, customEnumeration, required, randomised, numberingType });
      }
      this.#isSubmitted.set(false);
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.questionForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Update Question
    const question = assign(this.question, this.questionForm.value);
    this.slideAuthorFacade.requestUpdateQuestion(question, 'history.question_update').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.questionForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  onQuestionTypeSubmit() {
    if (this.#isSubmitted() || this.questionForm.invalid || !this.question || !this.questionForm.value.type) {
      return;
    }
    this.#isSubmitted.set(true);
    
    // Update Question Type
    this.slideAuthorFacade.requestChangeQuestionType(this.question, this.questionForm.value.type).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.questionForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
}
